import get from 'lodash/get'
import _last from 'lodash/last'

import { getReturnShipmentDestinationByState } from '@fielded/fs-api/lib/service/tools/territory-lookup'
import { getShipmentTypes, getShipmentConfirmEligibility } from '@fielded/fs-api/lib/shipment/tools'
import * as batchTools from '@fielded/fs-api/lib/batch/tools'

import { userIsAuthorised } from '../../../van-shared/utils/auth'
import { isReturnShipment, isShipmentStatusComplete } from '../../../common/utils/shipment'
import {
  toTitleCase,
  isShelflifeCoreApp,
  isShelflifeBasedApp,
  isPsmBasedApp,
  isPsmResellerApp,
  isPsmFacilityUser,
  isPsmWarehouseUser,
  isPsmDriverUser,
  isPsmResellerWarehouseUser,
  isPsmResellerFacilityUser,
  isEpnApp,
  canViewBatchInfo
} from '../../../van-shared/utils/utils'

// Allows batchIds with non-URL-safe chars, such as forward slashes.
// Since the other parts of an ID can’t have these chars, we only do encodeURIComponent
// on the batch number, for readability. Otherwise the URLS look like
// "/product%3Aopv%3Amanufacturer%3Abilthoven%3AbatchNo%3A111%2F123/send-confirmation"
// With this, they’re:
// "/product:opv:manufacturer:bilthoven:batchNo:111%2F123/send-confirmation"
// To decode, just use decodeURIComponent() on the whole ID
export const encodeBatchID = (batchID) => {
  let parts = batchID.split(':')
  parts[parts.length - 1] = encodeURIComponent(parts[parts.length - 1])
  return parts.join(':')
}

export const byId = (collection, prop = 'id') => collection
  .reduce((index, element) => {
    index[element[prop]] = element
    return index
  }, {})

export const batchIdToProductId = batchTools.batchIdToProductId
export const batchNoFromId = batchTools.batchNoFromId
export const isBatchId = batchTools.isBatchId

export const preferUnknown = prop => (a, b) => {
  // If neither batch is unknown, don’t interfere with sorting at all
  if (!a.id.match('unknown') && !b.id.match('unknown')) {
    return 0
  }
  if (a.id.match('unknown') && b.id.match('unknown')) {
    return 0
  }
  if (a.id.match('unknown') && !b.id.match('unknown')) {
    return -1
  } else {
    return 1
  }
}

export const isShipmentCompleteForWarehouseUser = (shipment, user) => {
  return shipment.status === 'received'
}

export const isShipmentComplete = (shipment, user) => {
  if (user && isFpUser(user) && shipment.status === 'sent' && isReturnShipment(user, shipment)) {
    return true
  }
  if (shipment.statusType === 'distribution') {
    // For drivers, only RECEIVED counts as completed (not SENT/ARRIVED)
    if (isPsmDriverUser(user)) {
      return shipment.status === 'received'
    }
    return isShipmentStatusComplete(shipment.status)
  }

  return shipment.status === 'received'
}

export const isDistributor = user => userIsAuthorised(user, 'feature:distributor')

export const isGlobalUser = user => userIsAuthorised(user, 'feature:global-shipments')

export const isFpUser = user => userIsAuthorised(user, 'feature:userRole:fp')

export const isOperatorUser = user => userIsAuthorised(user, 'feature:userRole:operator')

export const isPlannerUser = user => userIsAuthorised(user, 'feature:userRole:planner')

export const isExternalPlannerUser = user => userIsAuthorised(user, 'feature:userRole:external-planner')

export const isLastMileDeliveryUser = user => userIsAuthorised(user, 'feature:lastMileDeliveries')

// The national shipments list is what `feature:shipments` has always meant.
// Users who only hold `feature:lastMileDeliveries` (carriers) work off their own
// deliveries instead, so the list is not theirs to see. Safe as a plain role
// check because `getUserRoutes` only lets a user into this subapp when they hold
// one of the two roles, so "no shipments role" always implies last mile deliveries.
export const canViewNationalShipments = user => userIsAuthorised(user, 'feature:shipments')

export const getCanConfirmDelivery = async ({ api, user, shipment }) => {
  if (isPlannerUser(user)) {
    return false
  }
  if (!isOperatorUser(user)) {
    return true
  }

  const userLocation = get(user, 'location')

  // isOwnWarehouse and isImmediatePurchaseNew don't depend on destinationLocation,
  // so this first call can run before any location fetch.
  const { isOwnWarehouse, isImmediatePurchaseNew } = getShipmentConfirmEligibility({ shipment, userLocation })

  const canPackImmediatePurchase =
    isImmediatePurchaseNew &&
    isOwnWarehouse &&
    userIsAuthorised(user, 'feature:shipment:pack-immediate-purchase')

  if (canPackImmediatePurchase) {
    return true
  }

  let destinationLocation = await api.location.get(shipment.destination.id)
  if (!destinationLocation) {
    const locations = await api.location.getLocationsViaIds([shipment.destination.id])
    destinationLocation = locations[0]
  }

  const { isLastMileDelivery } = getShipmentConfirmEligibility({ shipment, destinationLocation, userLocation })

  const hasConfirmPermission = userIsAuthorised(user, 'feature:shipment:confirm')
  return hasConfirmPermission && !isLastMileDelivery && isOwnWarehouse
}

export const isPreAdvice = id => id.indexOf('status:pre-advice') > -1
export const isArrival = statusType => statusType === 'arrival'
export const isDistribution = statusType => statusType === 'distribution'

export const isNew = id => id.indexOf('status:new') > -1

export const isAdminUser = user => userIsAuthorised(user, 'feature:settings')

export const headerLocationFor = (shipment, config, isPsmManualShipment, defaultDestinationId) => {
  const pickDestination = defaultDestinationId || shipment.destination.id
  const destination = toTitleCase(pickDestination.split(':').pop())
  const { statusType } = shipment

  if (statusType === 'distribution' || (statusType === 'arrival' && isShelflifeBasedApp(config)) || isPsmManualShipment) {
    return destination
  }

  if (statusType === 'arrival' && shipment.origin.id === 'country') {
    return 'external'
  }

  return shipment.origin.id.split(':').pop()
}

const formatLocationName = (locationName) => {
  // Eg. "Rms-kigali-regional-warehouse" into "Rms Kigali Regional Warehouse"
  if (!locationName) return locationName
  return locationName
    .split('-') // split by hyphens
    .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each word
    .join(' ') // join with spaces
}

export const shipmentTitle = (shipment, config, isReturn, defaultDestinationId, defaultOriginId) => {
  const isShelflifeBased = isShelflifeBasedApp(config)
  const pickOrigin = defaultOriginId || shipment.origin.id
  const origin = formatLocationName(pickOrigin.split(':').pop())
  const isPsmManualShipment = shipment.statusType === 'arrival' && !isShelflifeBased
  const destination = formatLocationName(headerLocationFor(shipment, config, isPsmManualShipment, defaultDestinationId))
  if (isReturn) {
    return `Return from ${origin} to ${destination}`
  }
  if (isShelflifeBased && isPreAdvice(shipment.snapshotId)) {
    return 'Pre-advice'
  }
  const direction = (shipment.statusType === 'distribution' || isPsmManualShipment)
    ? 'Distribution to'
    : `Arrival ${isShelflifeBased ? 'to' : 'from'}`
  const title = `${direction} ${destination} from ${origin}`
  return title
}

export const getDefaultReturnDestination = (returnShipmentConfigEnabled, state) => {
  let destination
  if (returnShipmentConfigEnabled) {
    const configuredDestination = getReturnShipmentDestinationByState(state)
    if (configuredDestination) {
      destination = configuredDestination
    }
  }
  return destination
}

// TODO: human readable location names, see: fielded/van-orga#135
export const formatLocation = location => {
  if (location.lga) {
    return toTitleCase(location.lga) + ' LGA'
  }
  if (location.state) {
    return toTitleCase(location.state) + ' State'
  }
  if (location.zone) {
    return location.zone.toUpperCase()
  }
  if (location.id === 'national') {
    return 'National'
  }
  if (location.id === 'country') {
    return 'Country'
  }
  return location.id
}

export const isUnknownBatch = (batch) => !!batch.id.toLowerCase().match('batchno:unknown')

// TODO: this criteria is unstable and needs to be changed
export const isExternal = snapshotId => /^origin:country/.test(snapshotId)

export const getProductTotalFromPickList = (pickList, productName) => get(pickList, `product:${productName}.total`, 0)

export const numberWithCommas = (quantity) => {
  return quantity.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const shipmentTypeLabel = (shipmentType) => `${shipmentType.abbreviation} (${shipmentType.name})`

export const getShipmentTypeOptions = () => {
  const shipmentTypes = getShipmentTypes()
  return Object.keys(shipmentTypes).map(key => (
    { value: shipmentTypes[key].id, label: shipmentTypeLabel(shipmentTypes[key]) })
  )
}

export const getReceivedSnapshotComment = (shipment) => {
  if (!shipment.comments) return undefined

  const receivedComment = shipment.comments.find(comment =>
    comment.id.includes(':status:received:')
  )

  if (!receivedComment) return undefined

  return {
    comment: receivedComment.comment,
    signedBy: receivedComment.name,
    signature: receivedComment.signature,
    phoneNumber: receivedComment.phoneNumber
  }
}

export const getSentSnapshotComment = (shipment) => {
  if (!shipment.comments) return undefined

  const sentComment = shipment.comments.find(comment =>
    comment.id.includes(':status:sent:')
  )

  if (!sentComment) return undefined

  return {
    comment: sentComment.comment,
    signedBy: sentComment.name,
    signature: sentComment.signature,
    phoneNumber: sentComment.phoneNumber
  }
}

export const getLastSnapshotSignature = (shipment) => {
  const commentsWithSignatures = shipment.comments.filter(comment => comment.signature)
  return commentsWithSignatures.length === 0 ? undefined : commentsWithSignatures.pop().signature
}
export const getLastSnapshotSignedBy = (shipment) => {
  const commentsWithSignedBy = shipment.comments.filter(comment => comment.name)
  return commentsWithSignedBy.length === 0 ? undefined : _last(commentsWithSignedBy).name
}

export const getLastSnapshotComment = (shipment) => {
  const filledComments = shipment.comments.filter(comment => comment.comment)
  return filledComments.length === 0 ? undefined : _last(filledComments).comment
}

export const getTotalPackedItemVAT = (addVAT, item) => {
  return (addVAT && !!item.unitVat) ? item.packed * item.unitVat : 0
}

export const hasSkipValidationComment = (shipment) => {
  if (!shipment || !shipment.comments || !shipment.comments.length) {
    return false
  }
  return shipment.comments.some(comment => comment.comment === 'skip-validation')
}

export {
  isShelflifeCoreApp,
  isShelflifeBasedApp,
  isPsmBasedApp,
  isPsmResellerApp,
  isPsmFacilityUser,
  isPsmWarehouseUser,
  isPsmDriverUser,
  isPsmResellerWarehouseUser,
  isPsmResellerFacilityUser,
  isEpnApp,
  canViewBatchInfo
}
